import { useEffect, useMemo, useState } from "react";
import type { useIssues } from "../../controllers/useIssues";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import { UserInline } from "../components/UserCell";
import { ISSUE_STATUS_CATEGORY, type Issue } from "../../models/project";

// Month calendar of a project's issues, placed on their due date. Read-only overview (일정·캘린더):
// reuses the issues already loaded by useIssues — no extra API. Click an issue to see a compact panel.

const CAT_COLOR: Record<string, string> = {
  TODO: "#98A2B3",
  IN_PROGRESS: "#2E90FA",
  DONE: "#12B76A",
};
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function CalendarView({ issues }: { issues: ReturnType<typeof useIssues> }) {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState<Issue | null>(null);

  const dir = useUserDirectory(issues.getToken);
  useEffect(() => {
    dir.ensure(selected ? [selected.assigneeId] : []);
  }, [selected, dir.ensure]);

  const byDate = useMemo(() => {
    const map: Record<string, Issue[]> = {};
    for (const i of issues.issues) {
      if (!i.dueDate) continue;
      (map[i.dueDate] ??= []).push(i);
    }
    return map;
  }, [issues.issues]);

  // Build the 6x7 grid starting on the Sunday on/before the 1st.
  const cells = useMemo(() => {
    const first = new Date(ym.y, ym.m, 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [ym]);

  const todayStr = ymd(now);
  const undated = issues.issues.filter((i) => !i.dueDate).length;
  const monthLabel = `${ym.y}년 ${ym.m + 1}월`;
  const shift = (delta: number) =>
    setYm(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  return (
    <div className="cal">
      <div className="cal-head">
        <div className="cal-nav">
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => shift(-1)}>‹</button>
          <b className="cal-title">{monthLabel}</b>
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => shift(1)}>›</button>
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() })}>오늘</button>
        </div>
        <div className="cal-legend">
          <span><i className="cal-dot" style={{ background: CAT_COLOR.TODO }} /> 할 일</span>
          <span><i className="cal-dot" style={{ background: CAT_COLOR.IN_PROGRESS }} /> 진행 중</span>
          <span><i className="cal-dot" style={{ background: CAT_COLOR.DONE }} /> 완료</span>
          {undated > 0 && <span className="cal-undated">마감일 없음 {undated}건</span>}
        </div>
      </div>

      <div className="cal-grid">
        {WEEKDAYS.map((w) => (
          <div key={w} className="cal-wd">{w}</div>
        ))}
        {cells.map((d) => {
          const key = ymd(d);
          const inMonth = d.getMonth() === ym.m;
          const items = byDate[key] ?? [];
          return (
            <div key={key} className={`cal-cell ${inMonth ? "" : "muted"} ${key === todayStr ? "today" : ""}`}>
              <div className="cal-daynum">{d.getDate()}</div>
              <div className="cal-items">
                {items.slice(0, 4).map((i) => (
                  <button
                    key={i.id}
                    className="cal-chip"
                    style={{ borderLeftColor: CAT_COLOR[ISSUE_STATUS_CATEGORY[i.status]] }}
                    title={`${i.key} · ${i.summary}`}
                    onClick={() => setSelected(i)}
                  >
                    <span className="cal-chip-key">{i.key}</span> {i.summary}
                  </button>
                ))}
                {items.length > 4 && <div className="cal-more">+{items.length - 4}건</div>}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="cal-detail info-card">
          <div className="cal-detail-head">
            <b>{selected.key} · {selected.summary}</b>
            <button className="link-btn" onClick={() => setSelected(null)}>닫기</button>
          </div>
          <div className="kv"><span>상태</span><span>{selected.status}</span></div>
          <div className="kv"><span>유형</span><span>{selected.type}</span></div>
          <div className="kv"><span>마감일</span><span>{selected.dueDate ?? "-"}</span></div>
          <div className="kv"><span>스토리 포인트</span><span>{selected.storyPoints ?? "-"}</span></div>
          <div className="kv"><span>담당자</span><span>{selected.assigneeId ? <UserInline id={selected.assigneeId} dir={dir} /> : "미지정"}</span></div>
          <div style={{ marginTop: 8 }}>
            <button className="btn primary" style={{ width: "auto" }} onClick={() => issues.openIssue(selected.id)}>이슈 열기</button>
          </div>
        </div>
      )}
    </div>
  );
}
